import { useEffect, useRef, useState } from 'react';
import { useActive, useRemirrorContext } from '@remirror/react';
import { Bold, Italic, Strikethrough, Link2, Heading3, Undo, Redo, Image as ImageIcon, Video, Music, FileText, Instagram } from 'lucide-react';
import { TextSelection } from 'prosemirror-state';
import InsertVideoModal from '../InsertVideoModal';
import { createMediaFromUpload } from '../../../../../services/storage'
import { ButtonContainer, NewBadge, ToolbarContainer } from './styles'
import InsertLinkModal from '../InsertLinkModal';
import InsertMediaModal from '../InsertMediaModal';
import InsertInstagramReelsModal from '../InsertInstagramReelsModal';

interface ToolbarButtonProps {
  children: React.ReactNode
  onClick: () => void
  title: string
  isNew?: boolean
  active?: boolean
}


const ToolbarButton: React.FC<ToolbarButtonProps> = ({ children, onClick, title, isNew = false, active = false }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <ButtonContainer
      type="button"
      onClick={onClick}
      title={title}
      isHovered={isHovered}
      className={active ? 'active' : ''}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      {children}
      {isNew && <NewBadge>Nuevo</NewBadge>}
    </ButtonContainer>
  )
}

const useUploadHandler = (viewRef, commands, uploadSingleFileWithContext, nodeType) => {
  return async (file, titleFromModal?) => {
    const tempUrl = URL.createObjectURL(file);
    const uploadId = `upload_${Date.now()}`;


    const clearSelection = () => {
      // Clear any selection to prevent replacing focused media
      if (!viewRef.current) {
        return;
      }

      const { state, dispatch } = viewRef.current;

      // Simply clear the selection without moving cursor
      const tr = state.tr.setSelection(TextSelection.create(state.doc, state.selection.to));
      dispatch(tr);
    }

    const removeUploadingNode = () => {
      const { state, dispatch } = viewRef.current;
      const doc = state.doc;

      doc.descendants((node, pos) => {
        if (node.type.name === nodeType && node.attrs.id === uploadId) {
          const transaction = state.tr.delete(pos, pos + node.nodeSize);
          dispatch(transaction);
          return false;
        }
        return true;
      });
    }

    const insertCommands = {
      image: () => {
        // 🔧 FIX: Insert spacing before and image after to ensure proper separation
        const { state, dispatch } = viewRef.current;

        // Create nodes
        const spacingParagraph = state.schema.nodes.paragraph.create();
        const imageNode = state.schema.nodes.image.create({
          src: tempUrl,
          alt: 'Subiendo imagen...',
          id: uploadId,
          'data-uploading': true,
          'data-progress': 0,
        });

        // Insert spacing paragraph first, then image after it
        const pos = state.selection.to;
        const tr = state.tr
          .insert(pos, spacingParagraph)
          .insert(pos + 1, imageNode);

        dispatch(tr.scrollIntoView());
        clearSelection();
      },
      customVideo: () => {
        // 🔧 FIX: Insert spacing before and video after to ensure proper separation
        const { state, dispatch } = viewRef.current;

        // Create nodes
        const spacingParagraph = state.schema.nodes.paragraph.create();
        const videoNode = state.schema.nodes.customVideo.create({
          id: uploadId,
          src: null,
          filename: file.name,
          filesize: file.size,
          provider: 'local',
          'data-uploading': true,
          'data-progress': 0,
        });

        // Insert spacing paragraph first, then video after it
        const pos = state.selection.to;
        const tr = state.tr
          .insert(pos, spacingParagraph)
          .insert(pos + 1, videoNode);

        dispatch(tr.scrollIntoView());
        clearSelection();
      },
      customAudio: () => {
        // 🔧 FIX: Insert spacing before and audio after to ensure proper separation
        const { state, dispatch } = viewRef.current;

        // Create nodes
        const spacingParagraph = state.schema.nodes.paragraph.create();
        const audioNode = state.schema.nodes.customAudio.create({
          src: tempUrl,
          title: titleFromModal || file?.name,
          filename: file?.name,
          id: uploadId,
          'data-uploading': true,
          'data-progress': 0,
          isOwner: true,
          isCompressed: false,
          canBeCompressed: true,
        });

        // Insert spacing paragraph first, then audio after it
        const pos = state.selection.to;
        const tr = state.tr
          .insert(pos, spacingParagraph)
          .insert(pos + 1, audioNode);

        dispatch(tr.scrollIntoView());
        clearSelection();
      },
      customPdf: () => {
        // 🔧 FIX: Insert spacing before and PDF after to ensure proper separation
        const { state, dispatch } = viewRef.current;

        // Create nodes
        const spacingParagraph = state.schema.nodes.paragraph.create();
        const pdfNode = state.schema.nodes.customPdf.create({
          src: tempUrl,
          filename: titleFromModal || file?.name,
          title: 'Subiendo pdf...',
          id: uploadId,
          'data-uploading': true,
          'data-progress': 0
        });

        // Insert spacing paragraph first, then PDF after it
        const pos = state.selection.to;
        const tr = state.tr
          .insert(pos, spacingParagraph)
          .insert(pos + 1, pdfNode);

        dispatch(tr.scrollIntoView());
        clearSelection();
      }
    };

    // Insert the media first (this will be removed if upload fails)
    insertCommands[nodeType]();

    try {
      const uploadedFile = await uploadSingleFileWithContext(file, (progressEvent) => {
        try {
          const { tr } = viewRef.current.state;
          const doc = viewRef.current.state.doc;

          doc.descendants((node, pos) => {
            if (node.type.name === nodeType && node.attrs.id === uploadId) {
              const transaction = tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                'data-progress': Math.round(progressEvent),
              }).setMeta('addToHistory', false); // ⭐ This prevents adding to undo history

              viewRef.current.dispatch(transaction);
              return false;
            }
            return true;
          });
        } catch (error) {
        }
      });

      let mediaFileId;
      // //Insert the media not linked to any post until the post is created and has an ID)
      try {
        const mediaRes = await createMediaFromUpload(uploadedFile)
        mediaFileId = mediaRes.response?.id
      } catch (err) {
      }

      // Update the node with the final uploaded file info
      const { tr } = viewRef.current.state;
      const doc = viewRef.current.state.doc;

      doc.descendants((node, pos) => {
        if (node.type.name === nodeType && node.attrs.id === uploadId) {
          try {
            // 🔧 FIX: This final update should be EXCLUDED from history (so undo removes the entire media)
            const transaction = tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              src: uploadedFile.signed_url,
              'data-uploading': false,
              // 🔧 Don't set data-progress again - it's already 100% from the progress callback
              media_file_id: mediaFileId,
            }).setMeta('addToHistory', false); // ⭐ Exclude from history - only initial insertion should be undoable

            viewRef.current.dispatch(transaction);
            return false;
          } catch (error) {
            // Try to remove the uploading node if final update fails
            try {
              const deleteTransaction = tr.delete(pos, pos + node.nodeSize);
              viewRef.current.dispatch(deleteTransaction);
            } catch (deleteError) {
            }
            return false;
          }
        }
        return true;
      });

    } catch (err) {

      // Check if it's a upload limit error
      if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string' && err.message.includes('UPLOAD_LIMIT_EXCEEDED')) {
        removeUploadingNode();
      } else {
        // For other errors, also remove the node but show a different message
        removeUploadingNode();
      }
    }
  };
};

const Component = ({ uploadSingleFileWithContext, showToolbar, customToolbarItems = ['pdf', 'audio', 'video', 'image', 'instagram-reels'] }) => {
  const { commands, view } = useRemirrorContext();
  const viewRef = useRef(view);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedTextForLink, setSelectedTextForLink] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showInstagramReelsModal, setShowInstagramReelsModal] = useState(false);

  const active = useActive();

  const boldActive = active.bold();
  const italicActive = active.italic();
  const strikeActive = active.strike();
  const h3Active = active.heading({ level: 3 });
  const linkActive = active.link();

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const uploadImage = useUploadHandler(viewRef, commands, uploadSingleFileWithContext, 'image');
  const uploadVideo = useUploadHandler(viewRef, commands, uploadSingleFileWithContext, 'customVideo');
  const uploadAudio = useUploadHandler(viewRef, commands, uploadSingleFileWithContext, 'customAudio');
  const uploadPdf = useUploadHandler(viewRef, commands, uploadSingleFileWithContext, 'customPdf');

  const openFilePicker = (accept, callback) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.click();
    input.onchange = () => {
      if (input.files?.length) callback(input.files[0]);
    };
  };

  // Safe undo function with error handling - replicate handleKeyDown behavior
  const handleUndo = () => {
    try {
      const { state } = viewRef.current;

      // Validate document state before allowing undo
      if (!state.doc || !state.doc.content) {
        return;
      }

      // Check if document content is valid
      try {
        state.doc.check();
      } catch (docError) {
        return;
      }

      // If we get here, the document is valid - allow the operation
      try {
        commands.undo();
      } catch (undoError) {
      }
    } catch (error) {
    }
  };

  // Safe redo function with error handling
  const handleRedo = () => {
    try {
      const { state } = viewRef.current;

      // Comprehensive validation before allowing redo
      if (!state.doc || !state.doc.content) {
        return;
      }

      // Check if document content is valid
      try {
        state.doc.check();
      } catch (docError) {
        return;
      }

      try {
        commands.redo();
      } catch (redoError) {
      }
    } catch (error) {
    }
  };


  return (
    showToolbar && (
      <>
        <ToolbarContainer>
          <ToolbarButton onClick={() => commands.toggleBold()} active={boldActive} title="Bold"><Bold size={20} /></ToolbarButton>
          <ToolbarButton onClick={() => commands.toggleItalic()} active={italicActive} title="Italic"><Italic size={20} /></ToolbarButton>
          <ToolbarButton onClick={() => commands.toggleStrike()} active={strikeActive} title="Strike"><Strikethrough size={20} /></ToolbarButton>
          <ToolbarButton
            isNew={true}
            onClick={() => {
              // Obtener texto seleccionado
              const { state } = viewRef.current;
              const { from, to, empty } = state.selection;
              let selectedText = "";

              if (!empty) {
                selectedText = state.doc.textBetween(from, to);
              }

              setSelectedTextForLink(selectedText);
              setShowLinkModal(true);
            }}
            active={linkActive}
            title="Insert Link"
          >
            <Link2 size={20} />
          </ToolbarButton>
          <ToolbarButton onClick={() => commands.toggleHeading({ level: 3 })} active={h3Active} title="Heading 3"><Heading3 size={20} /></ToolbarButton>
          <ToolbarButton onClick={handleUndo} title="Undo"><Undo size={20} /></ToolbarButton>
          <ToolbarButton onClick={handleRedo} title="Redo"><Redo size={20} /></ToolbarButton>
          {customToolbarItems.includes("image") && <ToolbarButton isNew={true} onClick={() => openFilePicker('image/*', uploadImage)} title="Insert Image"><ImageIcon size={20} /></ToolbarButton>}
          {customToolbarItems.includes("video") && <ToolbarButton isNew={true} onClick={() => setShowVideoModal(true)} title="Insert Video"><Video size={20} /></ToolbarButton>}
          {customToolbarItems.includes("audio") && <ToolbarButton isNew={true} onClick={() => setShowAudioModal(true)} title="Insert Audio"><Music size={20} /></ToolbarButton>}
          {customToolbarItems.includes("pdf") && <ToolbarButton isNew={true} onClick={() => setShowPdfModal(true)} title="Insert PDF"><FileText size={20} /></ToolbarButton>}
          {customToolbarItems.includes("instagram-reels") && <ToolbarButton isNew={true} onClick={() => setShowInstagramReelsModal(true)} title="Insert Instagram Reel"><Instagram size={20} /></ToolbarButton>}
        </ToolbarContainer>

        {showLinkModal && (
          <InsertLinkModal
            isEditing={false}
            initialText={selectedTextForLink}
            onInsert={({ text, url }) => {
              const { state, dispatch } = viewRef.current;
              const { from, to, empty } = state.selection;

              if (empty) {
                // Si no hay selección, insertamos el nodo customLink
                commands.insertCustomLink({ href: url, text: text });
              } else {
                // Si hay selección, reemplazamos el texto seleccionado con el link
                const customLinkNode = state.schema.nodes.customLink.create({
                  href: url,
                  text: text
                });
                const tr = state.tr.replaceWith(from, to, customLinkNode);
                dispatch(tr.scrollIntoView());
              }
            }}
            onClose={() => {
              setShowLinkModal(false);
              setSelectedTextForLink("");
            }}
          />
        )}

        {showPdfModal && (
          <InsertMediaModal
            label="PDF"
            accept="application/pdf"
            onUpload={(file, title) => {
              uploadPdf(file, title);
              setShowPdfModal(false);
            }}
            onClose={() => setShowPdfModal(false)}
          />
        )}

        {showAudioModal && (
          <InsertMediaModal
            label="audio"
            accept="audio/mpeg,audio/ogg"
            onUpload={(file, title) => {
              uploadAudio(file, title);
              setShowAudioModal(false);
            }}
            onClose={() => setShowAudioModal(false)}
          />
        )}

        {showVideoModal && (
          <InsertVideoModal
            onInsertUrl={(url) => {
              let provider = 'local';
              if (url.includes('youtube.com') || url.includes('youtu.be')) provider = 'youtube';
              else if (url.includes('vimeo.com')) provider = 'vimeo';

              // 🔧 FIX: Insert spacing before and video after to ensure proper separation
              const spacingParagraph = view.state.schema.nodes.paragraph.create();
              const videoNode = view.state.schema.nodes.customVideo.create({
                src: url,
                provider,
                'data-uploading': false,
                'data-progress': null,
              });

              // Insert spacing paragraph first, then video after it
              const pos = view.state.selection.to;
              const tr = view.state.tr
                .insert(pos, spacingParagraph)
                .insert(pos + 1, videoNode);

              view.dispatch(tr.scrollIntoView());

              // Clear selection to prevent replacing the media
              const clearTr = view.state.tr.setSelection(TextSelection.create(view.state.doc, view.state.selection.to));
              view.dispatch(clearTr);

              setShowVideoModal(false);
            }}
            onUploadLocal={uploadVideo}
            onClose={() => setShowVideoModal(false)}
          />
        )}

        {showInstagramReelsModal && (
          <InsertInstagramReelsModal
            onInsertUrl={(url) => {
              // Insert Instagram Reel using the command
              commands.insertInstagramReel(url);
              setShowInstagramReelsModal(false);
            }}
            onClose={() => setShowInstagramReelsModal(false)}
          />
        )}
      </>
    )
  );
};

export default Component;
